/**
 * Recoverability Area (Spec 203 — US1 verification + score, US2 copy health)
 *
 * Renders inside the Backup command center: a verification policy toggle, the per-asset
 * recoverability scorecard (at-risk surfaced first, with reason + Verify-now), and the
 * secondary-copy health table. Built entirely from the canonical kit (@/components/ui) +
 * design tokens per the design blueprint (Constitution XLV).
 *
 * Author: Peter Dobler (Dobler Data Solutions)
 * Feature: 203-recoverability-assurance
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, AlertTriangle, Copy as CopyIcon, RefreshCw, Lock, Plus, Info } from 'lucide-react';
import {
  Card,
  DataTable,
  Badge,
  Button,
  Toggle,
  Select,
  Input,
  LoadingState,
  ErrorState,
  EmptyState,
} from '@/components/ui';
import { useAuth } from '../../hooks/useAuth';
import { useBackupActivity } from '../../store/backupActivityStore';
import {
  recoverabilityApi,
  RecoverabilityScore,
  VerificationRun,
  VerificationPolicy,
  BackupEntityType,
} from '../../api/recoverability';
import { formatEastern } from '@/utils/dateFormatter';

const statusVariant: Record<string, 'success' | 'warning' | 'info' | 'danger' | 'default'> = {
  Passed: 'success',
  Completed: 'success',
  Failed: 'danger',
  Running: 'info',
  Copying: 'info',
  Pending: 'default',
  Skipped: 'default',
  // Spec 246 US5 — honest recoverability verdicts (metadata proof).
  recoverable: 'success',
  degraded: 'warning',
  not_recoverable: 'danger',
  unverified: 'default',
  refused: 'default',
};

/** Spec 246 US5 — human labels for the honest verdict. */
const verdictLabel: Record<string, string> = {
  recoverable: 'Recoverable',
  degraded: 'Degraded',
  not_recoverable: 'Not recoverable',
  unverified: 'Unverified',
  refused: 'Refused',
};

/**
 * The four things "not proven recoverable" can mean, and how each should LOOK.
 *
 * Only `damaged` earns the danger treatment. `incomplete` is a gap in OUR measurement (a table
 * over the verify ceiling), `unproven` is an absence of evidence, and `empty` is an entity with
 * nothing to protect — none of those is a fault in the customer's data, and rendering them as if
 * they were is what made a healthy estate read as nineteen failures.
 */
const riskKindBadge: Record<string, { label: string; variant: 'success' | 'warning' | 'info' | 'danger' | 'default'; danger: boolean }> = {
  damaged: { label: 'Needs attention', variant: 'danger', danger: true },
  incomplete: { label: 'Check incomplete', variant: 'warning', danger: false },
  unproven: { label: 'Not yet proven', variant: 'default', danger: false },
  empty: { label: 'Nothing to back up', variant: 'default', danger: false },
};

/** Triage order: a real fault first, then unfinished work, then absent evidence, then the empties. */
const riskKindRank: Record<string, number> = {
  damaged: 4,
  incomplete: 3,
  unproven: 2,
  empty: 1,
  ok: 0,
};

const fmtRto = (s: number | null): string => {
  if (s == null) return '—';
  if (s < 90) return `${s}s`;
  if (s < 5400) return `${Math.round(s / 60)}m`;
  return `${(s / 3600).toFixed(1)}h`;
};
const fmtDate = (s: string | null): string => (s ? formatEastern(s) : '—');
const shortId = (id: string): string => `${id.slice(0, 8)}…`;
const hoursSince = (s: string | null): number => (s ? (Date.now() - new Date(s).getTime()) / 3_600_000 : Infinity);
/** The activity strip shows in-flight + recently-finished runs only — durable per-asset state lives
 *  in the scorecard below, so we don't permanently duplicate it here. */
const RECENT_WINDOW_HOURS = 24;

/** Asset cell: type badge + resolved entity name (GUID fallback) + workspace subtitle. */
const assetCell = (entityType: BackupEntityType, entityName: string | null, entityId: string, workspaceName: string | null): React.ReactElement => (
  <span className="flex flex-col">
    <span className="inline-flex items-center gap-1.5">
      <Badge variant={entityType === 'warehouse' ? 'info' : 'default'}>{entityType}</Badge>
      <span className="truncate font-medium text-[var(--color-text-primary)]" title={entityName ?? entityId}>
        {entityName ?? shortId(entityId)}
      </span>
    </span>
    {workspaceName && <span className="truncate text-xs text-[var(--color-text-tertiary)]">{workspaceName}</span>}
  </span>
);

function VerificationPolicyCard({ canManage }: { canManage: boolean }): React.ReactElement {
  const qc = useQueryClient();
  const { data: policy, isLoading } = useQuery({ queryKey: ['recoverability', 'policy'], queryFn: () => recoverabilityApi.policy() });
  const [draft, setDraft] = useState<Partial<VerificationPolicy> | null>(null);
  const eff = { ...(policy ?? { enabled: false, selection: 'newest', slaDays: 30, sampleN: null }), ...(draft ?? {}) } as VerificationPolicy;
  const save = useMutation({
    mutationFn: (p: Partial<VerificationPolicy>) => recoverabilityApi.savePolicy({ ...p, planId: null }),
    onSuccess: () => {
      setDraft(null);
      qc.invalidateQueries({ queryKey: ['recoverability', 'policy'] });
    },
  });

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-[var(--color-accent)]" />
          <span className="font-medium text-[var(--color-text-primary)]">Automated verification</span>
        </div>
        <Toggle
          checked={!!eff.enabled}
          disabled={!canManage || isLoading || save.isPending}
          onChange={(v) => save.mutate({ ...eff, enabled: v })}
          label={eff.enabled ? 'On' : 'Off'}
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--color-text-secondary)]">Select</span>
          <Select
            value={eff.selection}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...eff, selection: e.target.value as VerificationPolicy['selection'] })}
            options={[
              { value: 'newest', label: 'Newest point' },
              { value: 'oldest_newest', label: 'Oldest + newest' },
              { value: 'sample_n', label: 'Sample N' },
              { value: 'all', label: 'All points' },
            ]}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--color-text-secondary)]">SLA (days)</span>
          <Input
            type="number"
            className="w-20"
            value={String(eff.slaDays ?? 30)}
            disabled={!canManage}
            onChange={(e) => setDraft({ ...eff, slaDays: Number(e.target.value) })}
          />
        </div>
        {canManage && draft && (
          <Button variant="primary" size="sm" disabled={save.isPending} onClick={() => save.mutate(eff)}>
            Save
          </Button>
        )}
        <span className="grow" />
        <span className="text-xs text-[var(--color-text-tertiary)]">
          Verifies recovery points by restoring into throwaway targets (auto-deleted), bounded by the shared restore budget.
        </span>
      </div>
    </Card>
  );
}

// 🛑 Spec 270 FR-019a: `VaultsCard` DELETED (80 lines) -- it listed vaults and exposed the
// vault KV-secret name in a table column.

export const RecoverabilityArea: React.FC = () => {
  const { isOperatorOrAbove } = useAuth();
  const canManage = isOperatorOrAbove();
  const qc = useQueryClient();
  const [msg, setMsg] = useState<{ tone: 'info' | 'warn' | 'error'; text: string } | null>(null);
  const addActivity = useBackupActivity((s) => s.add);

  const scoresQ = useQuery({ queryKey: ['recoverability', 'scores'], queryFn: () => recoverabilityApi.scores() });
  // Recent verifications — poll while any are Running so the page shows live progress (the
  // container-level progress also streams in the Activity drawer via the shared executionName).
  const verifsQ = useQuery({
    queryKey: ['recoverability', 'verifications'],
    queryFn: async () => {
      // Operators finalize in-flight verifications on each poll so the row flips as soon as the
      // container finishes (the periodic sweep is the unattended fallback). Best-effort.
      if (canManage) {
        try {
          const r = await recoverabilityApi.reconcile();
          if (r.passed + r.failed > 0) qc.invalidateQueries({ queryKey: ['recoverability', 'scores'] });
        } catch {
          /* non-manager / transient — the sweep will reconcile */
        }
      }
      return recoverabilityApi.verifications();
    },
    refetchInterval: (q) => ((q.state.data as VerificationRun[] | undefined)?.some((v) => v.status === 'Running') ? 5000 : 20000),
  });
  // Activity strip = running + finished within the recent window (the scorecard holds durable state).
  const recentVerifs = (verifsQ.data ?? []).filter(
    (v) => v.status === 'Running' || hoursSince(v.completedAt ?? v.startedAt) <= RECENT_WINDOW_HOURS
  );
  const runningCount = recentVerifs.filter((v) => v.status === 'Running').length;

  const verify = useMutation({
    mutationFn: (r: RecoverabilityScore) => recoverabilityApi.verifyNow(r.entityType, r.entityId),
    onSuccess: (res, r) => {
      const label = r.entityName ?? shortId(r.entityId);
      if (res.status === 'running') {
        // Surface live container progress in the global Activity drawer (shared executionName).
        if (res.executionName) {
          addActivity({ executionName: res.executionName, verb: 'Verify', subject: `${r.entityType} ${label}`, entityType: r.entityType, entityId: r.entityId });
        }
        setMsg({
          tone: 'info',
          text: `Verification started for ${label} — restoring point ${shortId(res.runId)} into a throwaway target. Track it below or in Activity; the score updates when it finishes.`,
        });
      } else {
        setMsg({ tone: 'warn', text: `Verification skipped for ${label} — at the shared restore concurrency cap. It will retry on the next sweep.` });
      }
      qc.invalidateQueries({ queryKey: ['recoverability', 'scores'] });
      qc.invalidateQueries({ queryKey: ['recoverability', 'verifications'] });
    },
    onError: (e) => setMsg({ tone: 'error', text: e instanceof Error ? e.message : 'Verification failed to launch' }),
  });

  // Spec 270 FR-019a: the vault clean-room `recover` mutation is removed with its button.

  const msgTone =
    msg?.tone === 'error'
      ? 'border-[var(--color-danger)] text-[var(--color-danger)]'
      : msg?.tone === 'warn'
        ? 'border-[var(--color-status-warning)] text-[var(--color-text-primary)]'
        : 'border-[var(--color-accent)] text-[var(--color-text-primary)]';

  return (
    <div className="space-y-4">
      <VerificationPolicyCard canManage={canManage} />

      {msg && (
        <div className={`flex items-start gap-2 rounded-md border bg-[var(--color-bg-subtle)] px-3 py-2 text-sm ${msgTone}`}>
          <span className="grow">{msg.text}</span>
          <button type="button" className="shrink-0 text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)]" onClick={() => setMsg(null)} aria-label="Dismiss">
            ✕
          </button>
        </div>
      )}

      {/* Live + recent verifications strip (running + last 24h; durable state is the scorecard below) */}
      <Card
        padding="none"
        title={runningCount > 0 ? `Active verifications — ${runningCount} running` : 'Recent verifications (last 24h)'}
      >
        {verifsQ.isLoading ? (
          <LoadingState rows={3} />
        ) : recentVerifs.length === 0 ? (
          <EmptyState
            title="No verifications in the last 24 hours"
            message="Click Verify on an asset (or enable automated verification). Each run restores the recovery point into a throwaway target, validates it, and auto-deletes — it appears here while running and for 24h after; the lasting result is in the scorecard below."
          />
        ) : (
          <DataTable<VerificationRun>
            minWidth="62rem"
            columns={[
              {
                key: 'entityId',
                header: 'Asset',
                render: (v) => assetCell(v.entityType, v.entityName, v.entityId, v.workspaceName),
              },
              { key: 'runId', header: 'Point', width: '7rem', render: (v) => <span className="font-mono text-xs">{shortId(v.runId)}</span> },
              {
                key: 'status',
                header: 'Status',
                width: '8rem',
                render: (v) =>
                  v.status === 'Running' ? (
                    <span className="inline-flex items-center gap-1.5 text-[var(--color-accent)]">
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Running
                    </span>
                  ) : (
                    <Badge variant={statusVariant[v.status] ?? 'default'}>{v.status}</Badge>
                  ),
              },
              { key: 'resultDetail', header: 'Detail', render: (v) => <span className="text-xs text-[var(--color-text-secondary)]">{v.resultDetail ?? (v.status === 'Running' ? 'restoring into throwaway target…' : '—')}</span> },
              { key: 'observedRtoSeconds', header: 'Obs. RTO', width: '6rem', align: 'right', render: (v) => fmtRto(v.observedRtoSeconds) },
              { key: 'startedAt', header: 'Started', width: '11rem', sortable: true, render: (v) => fmtDate(v.startedAt) },
              { key: 'completedAt', header: 'Finished', width: '11rem', render: (v) => fmtDate(v.completedAt) },
            ]}
            rows={recentVerifs}
            rowKey={(v) => v.id}
          />
        )}
      </Card>

      {/* Recoverability scorecard */}
      <Card padding="none" title="Recoverability scorecard">
        {scoresQ.isLoading ? (
          <LoadingState rows={5} />
        ) : scoresQ.isError ? (
          <ErrorState onRetry={() => scoresQ.refetch()} />
        ) : !scoresQ.data || scoresQ.data.length === 0 ? (
          <EmptyState
            title="No verifications yet"
            message="Enable automated verification above, or click Verify on an asset once it has a recovery point. The metadata proof verifies cardinality, catalog and file presence — countable proof of recoverability, not cell-value correctness."
          />
        ) : (
          <DataTable<RecoverabilityScore>
            minWidth="64rem"
            columns={[
              {
                key: 'entityId',
                header: 'Asset',
                render: (r) => assetCell(r.entityType, r.entityName, r.entityId, r.workspaceName),
              },
              {
                key: 'atRisk',
                header: 'Status',
                width: '16rem',
                sortable: true,
                // 🛑 Lead with the RISK KIND, not the verdict word. The verdict answers "what did the
                // proof output"; the kind answers "is this my problem, and of what sort" — and only
                // the second is actionable. Showing `degraded` for both a missing file and a table
                // that merely exceeded the verify ceiling is how one real fault came to sit in a
                // list of nineteen. The verdict stays as secondary context, never as the headline.
                render: (r) => {
                  const kind = r.riskKind;
                  const state = r.recoverabilityState;
                  const badge = kind
                    ? riskKindBadge[kind]
                    : { label: 'Recoverable', variant: 'success' as const, danger: false };
                  return (
                    <span className="inline-flex items-center gap-1.5">
                      {badge.danger && <AlertTriangle className="h-4 w-4 text-[var(--color-danger)]" />}
                      <Badge variant={badge.variant}>{badge.label}</Badge>
                      {r.atRiskReason && (
                        <span className="text-xs text-[var(--color-text-secondary)]">({r.atRiskReason})</span>
                      )}
                      {state && kind && (
                        <span className="text-[10px] uppercase tracking-wide text-[var(--color-text-secondary)]">
                          {verdictLabel[state] ?? state}
                        </span>
                      )}
                    </span>
                  );
                },
              },
              {
                key: 'lastStatus',
                header: 'Last',
                width: '6.5rem',
                render: (r) => (r.lastStatus ? <Badge variant={statusVariant[r.lastStatus] ?? 'default'}>{r.lastStatus}</Badge> : <span>—</span>),
              },
              { key: 'passRate', header: 'Pass rate', width: '6.5rem', align: 'right', sortable: true, render: (r) => (r.passRate == null ? '—' : `${r.passRate}%`) },
              { key: 'observedRtoSeconds', header: 'Obs. RTO', width: '6rem', align: 'right', render: (r) => fmtRto(r.observedRtoSeconds) },
              { key: 'lastVerifiedAt', header: 'Last verified', width: '12rem', sortable: true, render: (r) => fmtDate(r.lastVerifiedAt) },
              {
                key: 'actions',
                header: '',
                width: '7rem',
                align: 'right',
                sortable: false,
                render: (r) =>
                  canManage ? (
                    <Button variant="outline" size="sm" disabled={verify.isPending} onClick={() => verify.mutate(r)}>
                      <RefreshCw className="h-4 w-4" /> Verify
                    </Button>
                  ) : null,
              },
            ]}
            // Sort by SEVERITY of the kind, not by the at-risk bit — otherwise the one entity that
            // needs a human sits somewhere among eighteen that do not, in no particular order.
            rows={[...scoresQ.data].sort(
              (a, b) => (riskKindRank[b.riskKind ?? 'ok'] ?? 0) - (riskKindRank[a.riskKind ?? 'ok'] ?? 0)
            )}
            rowKey={(r) => `${r.entityType}:${r.entityId}`}
          />
        )}
      </Card>

      {/* 🛑 Spec 270 FR-019a — the "Secondary copy health (3-2-1-1-0)" card and the
          <VaultsCard /> panel are REMOVED. Both rendered a feature that no longer exists: the copy
          table read /recoverability/copy-health and the vault card read /recoverability/vaults,
          eight routes that are gone and two tables migration 280 drops.

          🛑 Deliberately removed rather than left to render an empty state. An EmptyState reading
          "No secondary copies" is a MEASUREMENT -- it tells an operator the feature is on and found
          nothing, which is exactly the false assurance this spec exists to eliminate. The honest
          rendering of a removed capability is its absence. */}
    </div>
  );
};

export default RecoverabilityArea;
